import { useEffect } from 'react';
import {
  GoFormzAgentContextTypes,
  GoFormzAgentLocalState,
  GoFormzAgentTemplateContext,
  GoFormzAgentWorkflowContext,
  GoFormzAgentDatasourceContext,
  GoFormzAgentFormContext
} from './goFormz-agent-types';
import { useImmerState } from '../hooks/useImmerState';
import { useGoFormzAgentContext } from './GoFormzAgentProvider';
import { DockPosition, DockMode } from './GoFormzAgentPanel';
import { AppFeatures, canIUse, Features } from '../helpers/PermissionUtility';

const DEFAULT_STATE: GoFormzAgentLocalState = {
  loading: false,
  isDirty: false,
  [GoFormzAgentContextTypes.Template]: {
    templateId: '',
    selectedFieldIds: [],
    selectedProperty: '',
    selectedCalculation: '',
    suggestedCalculationSelected: ''
  },
  [GoFormzAgentContextTypes.Workflow]: {},
  [GoFormzAgentContextTypes.Datasource]: {},
  [GoFormzAgentContextTypes.Form]: {},
  errorMessage: null
};

export interface GoFormzAgentHook {
  loading: boolean;
  templateContext: GoFormzAgentTemplateContext;
  workflowContext: GoFormzAgentWorkflowContext;
  datasourceContext: GoFormzAgentDatasourceContext;
  formContext: GoFormzAgentFormContext;
  errorMessage: string | null;
  setTemplateContext: (context: GoFormzAgentTemplateContext) => Promise<number>;
  openAgent: () => void;
  closeAgent: () => void;
  isAgentOpen: boolean;
  selectFormula: (formula: string) => void;
  selectedFormula: string;
  goPilotEnabled: boolean;
  goPilotTemplateBuildingEnabled: boolean;
  goPilotCalculationsEnabled: boolean;
  dockPosition: DockPosition;
  dockMode: DockMode;
  panelSize: { width: number; height: number };
  setDockPosition: (position: DockPosition) => void;
  setDockMode: (mode: DockMode) => void;
  setPanelSize: (size: { width: number; height: number }) => void;
}

export function useGoFormzAgent({ showErrorMessage = true } = {}): GoFormzAgentHook {
  // Get state from context provider
  const {
    isAgentOpen,
    selectedFormula,
    templateContext: sharedTemplateContext,
    dockPosition,
    dockMode,
    panelSize,
    openAgent,
    closeAgent,
    selectFormula,
    setTemplateContext,
    setDockPosition,
    setDockMode,
    setPanelSize
  } = useGoFormzAgentContext();

  const [state] = useImmerState(DEFAULT_STATE);
  const { loading, workflowContext, datasourceContext, formContext, errorMessage } = state;

  // Feature flags - evaluate here where we have proper Redux access
  const goPilotEnabled = canIUse(Features.GoPilot);
  const goPilotCalculationsEnabled = canIUse(AppFeatures.GoPilotCalculationBuilderAccess);
  const goPilotTemplateBuildingEnabled = canIUse(AppFeatures.GoPilotTemplateBuilderAccess);

  // Show error messages if enabled
  useEffect(() => {
    if (showErrorMessage && errorMessage) {
      console.error(errorMessage);
    }
  }, [showErrorMessage, errorMessage]);

  // Control Intercom visibility based on agent state
  useEffect(() => {
    if (window.Intercom) {
      // Hide/show the Intercom launcher icon based on agent state
      window.Intercom('update', { hide_default_launcher: isAgentOpen });
    }
  }, [isAgentOpen]);

  // Return the same interface as before, but now powered by the context
  return {
    loading,
    templateContext: sharedTemplateContext,
    workflowContext,
    datasourceContext,
    formContext,
    errorMessage,
    setTemplateContext,
    closeAgent,
    isAgentOpen,
    openAgent,
    selectFormula,
    selectedFormula,
    goPilotEnabled,
    goPilotTemplateBuildingEnabled,
    goPilotCalculationsEnabled,
    dockPosition,
    dockMode,
    panelSize,
    setDockPosition,
    setDockMode,
    setPanelSize
  };
}
